import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import TermsGate from "@/components/TermsGate";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async (attempt = 0) => {
    const token = localStorage.getItem("ss_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setLoading(false);
    } catch (err) {
      if (err?.response?.status === 401) {
        // Genuine expired/invalid token — the api interceptor already cleared it
        // and dispatched ss:session-expired (which shows the re-login message).
        setUser(null);
        setLoading(false);
      } else {
        // Transient failure (backend restarting / unavailable / 5xx / network).
        // Keep the token and retry with backoff — never log the user out for an outage.
        if (attempt < 6) {
          setTimeout(() => loadMe(attempt + 1), Math.min(1500 * 2 ** attempt, 15000));
        } else {
          setLoading(false);
        }
      }
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      toast.error("Your session has expired, please log in again");
    };
    window.addEventListener("ss:session-expired", onExpired);
    return () => window.removeEventListener("ss:session-expired", onExpired);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ss_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("ss_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("ss_token");
    setUser(null);
  };

  const refreshUser = loadMe;

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refreshUser }}>
      {children}
      {user && <TermsGate key={user.id} />}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
